<template>
  <div class="teacher-app-page student-profile-page" v-if="student">
    <Toolbar />

    <v-container class="student-profile" fluid>
      <header class="student-profile-hero">
        <div class="student-profile-hero__top">
          <button type="button" class="student-profile-back" aria-label="Back to students" @click="close">
            <v-icon size="20">mdi-chevron-left</v-icon>
          </button>
          <div class="student-profile-hero__actions">
            <button type="button" @click="edit"><v-icon size="18">mdi-pencil-outline</v-icon><span>Edit</span></button>
            <v-menu offset-y left content-class="student-profile-action-menu">
              <template v-slot:activator="{ on, attrs }">
                <button v-bind="attrs" v-on="on" type="button" aria-label="More student actions">
                  <v-icon size="20">mdi-dots-horizontal</v-icon>
                </button>
              </template>
              <v-list dense>
                <v-list-item @click="removeFromClass">
                  <v-list-item-icon><v-icon color="#8a6525">mdi-account-minus-outline</v-icon></v-list-item-icon>
                  <v-list-item-content><v-list-item-title>Remove from class</v-list-item-title></v-list-item-content>
                </v-list-item>
                <v-list-item @click="deleteStudent">
                  <v-list-item-icon><v-icon color="#b94a4a">mdi-delete-outline</v-icon></v-list-item-icon>
                  <v-list-item-content><v-list-item-title class="red--text text--darken-1">Delete student</v-list-item-title></v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>

        <div class="student-profile-hero__identity">
          <span class="student-profile-avatar">{{ initials(student.Student.fullName) }}</span>
          <div>
            <p>Student profile</p>
            <h1>{{ student.Student.fullName }}</h1>
            <div class="student-profile-meta">
              <span v-if="student.Student.localId !== null && student.Student.localId !== undefined" class="student-profile-local-id">#{{ student.Student.localId }}</span>
              <span v-if="studentDob" class="student-profile-dob">
                <v-icon size="14" color="#2f7d4a">mdi-cake-variant-outline</v-icon>
                {{ studentDob }}<em v-if="studentAge">{{ studentAge }}</em>
              </span>
              <span v-else class="student-profile-dob student-profile-dob--missing">
                <v-icon size="14">mdi-calendar-blank-outline</v-icon>DOB not provided
              </span>
              <span v-if="selectedClassRoom && selectedClassRoom.ClassRoom">
                <v-icon size="14" color="#2f7d4a">mdi-google-classroom</v-icon>
                {{ selectedClassRoom.ClassRoom.name }}
              </span>
            </div>
          </div>
        </div>
      </header>

      <section v-if="customer" class="student-profile-section">
        <div class="student-profile-section__heading">
          <div><p class="teacher-page-eyebrow">Family</p><h2>Parent contacts</h2></div>
          <small>Tap an action to connect</small>
        </div>

        <div class="student-contact-grid">
          <article v-if="hasDadContact" class="student-contact-card">
            <div class="student-contact-card__header">
              <span class="student-contact-card__person">{{ initials(customer.fatherName || "Dad") }}</span>
              <div><small>Dad</small><strong>{{ customer.fatherName || "Father" }}</strong></div>
            </div>
            <a v-if="customer.fatherPhoneNumber" class="student-contact-card__value" :href="`tel:${customer.fatherPhoneNumber}`">
              {{ customer.fatherPhoneNumber | USPhoneNumber }}
            </a>
            <a v-if="customer.fatherEmailAddress" class="student-contact-card__email" :href="`mailto:${customer.fatherEmailAddress}`">
              {{ customer.fatherEmailAddress }}
            </a>
            <div v-if="customer.fatherPhoneNumber" class="student-contact-actions">
              <a :href="`tel:${customer.fatherPhoneNumber}`" aria-label="Call Dad"><v-icon>mdi-phone-outline</v-icon><span>Call</span></a>
              <a :href="`sms:${customer.fatherPhoneNumber}`" aria-label="Text Dad"><v-icon>mdi-message-text-outline</v-icon><span>Text</span></a>
              <a :href="whatsappLink(customer.fatherPhoneNumber)" target="_blank" rel="noopener" aria-label="WhatsApp Dad"><v-icon>mdi-whatsapp</v-icon><span>WhatsApp</span></a>
              <a v-if="customer.fatherEmailAddress" :href="`mailto:${customer.fatherEmailAddress}`" aria-label="Email Dad"><v-icon>mdi-email-outline</v-icon><span>Email</span></a>
            </div>
          </article>

          <article v-if="hasMomContact" class="student-contact-card">
            <div class="student-contact-card__header">
              <span class="student-contact-card__person student-contact-card__person--mom">{{ initials(customer.motherName || "Mom") }}</span>
              <div><small>Mom</small><strong>{{ customer.motherName || "Mother" }}</strong></div>
            </div>
            <a v-if="customer.motherPhoneNumber" class="student-contact-card__value" :href="`tel:${customer.motherPhoneNumber}`">
              {{ customer.motherPhoneNumber | USPhoneNumber }}
            </a>
            <a v-if="customer.motherEmailAddress" class="student-contact-card__email" :href="`mailto:${customer.motherEmailAddress}`">
              {{ customer.motherEmailAddress }}
            </a>
            <div v-if="customer.motherPhoneNumber" class="student-contact-actions">
              <a :href="`tel:${customer.motherPhoneNumber}`" aria-label="Call Mom"><v-icon>mdi-phone-outline</v-icon><span>Call</span></a>
              <a :href="`sms:${customer.motherPhoneNumber}`" aria-label="Text Mom"><v-icon>mdi-message-text-outline</v-icon><span>Text</span></a>
              <a :href="whatsappLink(customer.motherPhoneNumber)" target="_blank" rel="noopener" aria-label="WhatsApp Mom"><v-icon>mdi-whatsapp</v-icon><span>WhatsApp</span></a>
              <a v-if="customer.motherEmailAddress" :href="`mailto:${customer.motherEmailAddress}`" aria-label="Email Mom"><v-icon>mdi-email-outline</v-icon><span>Email</span></a>
            </div>
          </article>
        </div>
      </section>

      <section v-if="customer && (customer.address || customer.emergencyPhoneNumber)" class="student-profile-section student-details-grid">
        <button v-if="customer.address" type="button" class="student-detail-card" @click="openGoogleMap">
          <span><v-icon color="#2f7d4a">mdi-map-marker-outline</v-icon></span>
          <span><small>Home address</small><strong>{{ customer.address }}</strong><em>{{ locationLine }}</em></span>
          <v-icon size="18" color="#96a29a">mdi-open-in-new</v-icon>
        </button>

        <article v-if="customer.emergencyPhoneNumber" class="student-detail-card">
          <span class="student-detail-card__emergency"><v-icon color="#b94a4a">mdi-alert-circle-outline</v-icon></span>
          <span><small>Emergency contact</small><strong>{{ customer.emergencyName || "Emergency contact" }}</strong><em>{{ customer.emergencyRelation }}</em></span>
          <div class="student-detail-card__actions">
            <a :href="`tel:${customer.emergencyPhoneNumber}`" aria-label="Call emergency contact"><v-icon size="19">mdi-phone-outline</v-icon></a>
            <a :href="`sms:${customer.emergencyPhoneNumber}`" aria-label="Text emergency contact"><v-icon size="19">mdi-message-text-outline</v-icon></a>
            <a :href="whatsappLink(customer.emergencyPhoneNumber)" target="_blank" rel="noopener" aria-label="WhatsApp emergency contact"><v-icon size="19">mdi-whatsapp</v-icon></a>
          </div>
        </article>
      </section>

      <section class="student-profile-section student-attendance-card">
        <div class="student-profile-section__heading">
          <div><p class="teacher-page-eyebrow">Overview</p><h2>Attendance</h2></div>
          <span class="student-attendance-card__rate">{{ student.per || 0 }}%</span>
        </div>
        <v-progress-linear :value="student.per || 0" height="7" rounded color="primary" background-color="#e7eee9" />
        <div class="student-attendance-stats">
          <div><strong>{{ student.totalPresentAttendance || 0 }}</strong><span>Present</span></div>
          <div><strong>{{ student.totalAbsentAttendance || 0 }}</strong><span>Absent</span></div>
          <div><strong>{{ student.totalLateAttendance || 0 }}</strong><span>Late</span></div>
          <div><strong>{{ student.totalLeaveAttendance || 0 }}</strong><span>Leave</span></div>
        </div>
        <small class="student-attendance-card__total">Based on {{ student.totalAttendance || 0 }} attendance records</small>
      </section>

      <button type="button" class="student-report-card-link" @click="openReportCardList">
        <span><v-icon color="#3c6e9c">mdi-file-document-outline</v-icon></span>
        <span><strong>Report cards</strong><small>View saved report cards for this student</small></span>
        <v-icon size="19" color="#96a29a">mdi-chevron-right</v-icon>
      </button>
    </v-container>

    <Edit @submit="close" @close="editDialog = !editDialog" :editStudent="student.Student" :editDialog="editDialog" />

    <v-dialog v-model="reportCardListDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dense flat color="white" class="teacher-dialog-toolbar">
          <v-btn icon @click="reportCardListDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title>Report cards</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="student-report-dialog">
          <div class="student-report-dialog__heading">
            <div>
              <p class="teacher-page-eyebrow">Student records</p>
              <h2>{{ student.Student.fullName }}</h2>
              <p>Choose a saved report card to preview it.</p>
            </div>
            <span v-if="!loadingReportCards && reportCards.length" class="student-report-dialog__count">
              {{ reportCards.length }} {{ reportCards.length === 1 ? "report" : "reports" }}
            </span>
          </div>
          <div v-if="loadingReportCards" class="teacher-loading-state"><v-progress-circular size="36" width="3" color="primary" indeterminate /><strong>Loading report cards</strong></div>
          <div v-else-if="reportCards.length" class="student-report-list">
            <button v-for="item in reportCards" :key="item.id" type="button" class="student-report-list__item" @click="openReportCardPdf(item)">
              <span class="student-report-list__icon"><v-icon color="#3c6e9c">mdi-file-document-outline</v-icon></span>
              <span class="student-report-list__details">
                <strong>{{ item.Year ? item.Year.name : "Report card" }}</strong>
                <small>Generated {{ item.generatedAt | moment("LLL") }}</small>
              </span>
              <span class="student-report-list__action">View <v-icon size="18">mdi-chevron-right</v-icon></span>
            </button>
          </div>
          <div v-else class="teacher-empty-state"><span><v-icon size="28" color="#2f7d4a">mdi-file-document-outline</v-icon></span><h3>No report cards yet</h3><p>Saved report cards will appear here.</p></div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="reportCardPreviewDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dense flat color="white" class="teacher-dialog-toolbar">
          <v-btn icon @click="closePdfPreview"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title>Report card preview</v-toolbar-title>
          <v-spacer />
          <v-btn v-if="reportCardPdfUrl" icon :href="reportCardPdfUrl" target="_blank"><v-icon>mdi-open-in-new</v-icon></v-btn>
        </v-toolbar>
        <iframe v-if="reportCardPdfUrl" :src="reportCardPdfUrl" class="student-report-iframe"></iframe>
      </v-card>
    </v-dialog>

    <BottomNavigation />
  </div>
</template>

<script>
import studentService from "@/modules/Students/service.js";
import classRoomService from "@/modules/ClassRoom/service.js";
import reportCardService from "@/modules/ReportCard/service.js";
import Edit from "@/modules/Students/Edit.vue";
import Toolbar from "@/modules/Layout/components/Toolbar.vue";
import BottomNavigation from "@/modules/Layout/components/BottomNavigation.vue";
import pdfMake from "pdfmake/build/pdfmake.min";
import { pdfMake as pdfFonts } from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.vfs;

export default {
  name: "teacher-student-profile",
  components: { Edit, Toolbar, BottomNavigation },
  data() {
    return {
      editDialog: false,
      reportCardListDialog: false,
      reportCardPreviewDialog: false,
      reportCards: [],
      loadingReportCards: false,
      reportCardPdfUrl: null,
      student: null,
      id: null,
      ClassRoomId: null,
      selectedClassRoom: null,
    };
  },
  created() {
    if (Object.keys(this.$route.params).length === 0) this.$router.push({ name: "home" });
    else {
      this.student = this.$route.params.student;
      this.selectedClassRoom = this.$route.params.selectedClassRoom;
      document.title = `taleem - ${this.student.Student.fullName}`;
    }
  },
  beforeDestroy() {
    this.closePdfPreview();
  },
  computed: {
    customer() {
      return this.student && this.student.Student ? this.student.Student.Customer : null;
    },
    hasDadContact() {
      return this.customer && (this.customer.fatherName || this.customer.fatherPhoneNumber || this.customer.fatherEmailAddress);
    },
    hasMomContact() {
      return this.customer && (this.customer.motherName || this.customer.motherPhoneNumber || this.customer.motherEmailAddress);
    },
    locationLine() {
      if (!this.customer) return "";
      return [this.customer.city, this.customer.state, this.customer.zip].filter(Boolean).join(", ");
    },
    studentDob() {
      const dob = this.student && this.student.Student ? this.student.Student.dob : null;
      return dob && this.$moment(dob).isValid() ? this.$moment(dob).format("M/D/YYYY") : "";
    },
    studentAge() {
      const dob = this.student && this.student.Student ? this.student.Student.dob : null;
      if (!dob || !this.$moment(dob).isValid() || this.$moment(dob).isAfter(this.$moment(), "day")) return "";
      const years = this.$moment().diff(this.$moment(dob), "years");
      return years < 1 ? "Under 1 year" : `${years} ${years === 1 ? "year" : "years"} old`;
    },
  },
  methods: {
    initials(name) {
      if (!name) return "S";
      return name.split(" ").filter(Boolean).slice(0, 2).map(part => part.charAt(0)).join("").toUpperCase();
    },
    whatsappLink(phone) {
      let number = String(phone || "").replace(/\D/g, "");
      if (number.length === 10) number = `1${number}`;
      return `https://wa.me/${number}`;
    },
    edit() {
      this.editDialog = !this.editDialog;
    },
    async openReportCardList() {
      this.reportCardListDialog = true;
      await this.getReportCards();
    },
    async getReportCards() {
      try {
        this.loadingReportCards = true;
        const response = await reportCardService.getSavedStudentReport({ StudentId: this.student.Student.id });
        this.reportCards = response.data || [];
      } catch (error) {
        console.log("error fetching report cards", error);
        this.reportCards = [];
      } finally {
        this.loadingReportCards = false;
      }
    },
    async openReportCardPdf(item) {
      if (!item) return;

      this.closePdfPreview();

      if (item.pdfBase64) {
        const mimeType = item.pdfMimeType || "application/pdf";
        this.reportCardPdfUrl = `data:${mimeType};base64,${item.pdfBase64}`;
        this.reportCardPreviewDialog = true;
        return;
      }

      const docDefinition =
        item.snapshotMeta && item.snapshotMeta.docDefinition;

      if (!docDefinition) {
        this.$swal({
          icon: "info",
          title: "Report card unavailable",
          text: "This saved report card does not contain preview data. Regenerate it from the admin panel, then try again.",
        });
        return;
      }

      try {
        const blob = await new Promise((resolve, reject) => {
          try {
            pdfMake.createPdf(docDefinition).getBlob(resolve);
          } catch (error) {
            reject(error);
          }
        });
        this.reportCardPdfUrl = URL.createObjectURL(blob);
        this.reportCardPreviewDialog = true;
      } catch (error) {
        console.log("error creating report card PDF", error);
        this.$swal({
          icon: "error",
          title: "Unable to open report card",
          text: "The saved report card could not be rendered. Please try again.",
        });
      }
    },
    closePdfPreview() {
      this.reportCardPreviewDialog = false;
      if (
        this.reportCardPdfUrl &&
        this.reportCardPdfUrl.indexOf("blob:") === 0
      ) {
        URL.revokeObjectURL(this.reportCardPdfUrl);
      }
      this.reportCardPdfUrl = null;
    },
    openGoogleMap() {
      const address = [this.customer.address, this.customer.city, this.customer.state, this.customer.zip].filter(Boolean).join(" ");
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, "_blank");
    },
    removeFromClass() {
      this.$swal({
        title: "Remove from class?",
        text: `${this.student.Student.fullName} will be removed from this class, but their student record will remain.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#8a6525",
        cancelButtonColor: "#2f7d4a",
        cancelButtonText: "Keep student",
        confirmButtonText: "Remove from class",
      }).then(result => {
        if (result.value) {
          classRoomService.removeStudent(this.student.id).then(response => {
            if (response.data) this.$router.push({ name: "home" });
          });
        }
      });
    },
    deleteStudent() {
      this.$swal({
        title: "Delete student?",
        text: `This permanently deletes ${this.student.Student.fullName}'s student record. This cannot be undone.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#b94a4a",
        cancelButtonText: "Cancel",
        confirmButtonText: "Delete permanently",
      }).then(result => {
        if (result.value) {
          studentService.delete(this.student.Student.id).then(() => this.$router.push({ name: "home" }));
        }
      });
    },
    close() {
      this.$router.push({ name: "home" });
    },
  },
};
</script>
